import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, HardHat, Compass, Package } from "lucide-react";
import axios from "axios";
import { WELLSIDE_FEATURES } from "@/data/wellsides";
import { SEO, buildProductJsonLd, buildBreadcrumbJsonLd } from "@/components/SEO";
import { HeroImage } from "@/components/HeroImage";
import { HoverImage } from "@/components/HoverImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductOptions } from "@/components/ProductOptions";
import { HeroTrustBadgesV2 } from "@/components/HeroTrustBadgesV2";
// import { CompetitorComparisonTable } from "@/components/CompetitorComparisonTable"; // TODO: re-enable once competitor data is reviewed (see ROADMAP.md)
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";
import { WhyItsDifferentSection } from "@/components/WhyItsDifferentSection";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ");

// Renders "**bold**" markers inside a key-feature line as bold white spans
const renderBoldSegments = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="text-white font-semibold">{part}</strong> : part
  );

export default function WellsideDetailPage() {
  const { slug } = useParams();
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/wellsides`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const model = models?.find((m) => m.slug === slug);
  const hasOptions = model?.optionGroups?.length > 0;
  const [optionsState, setOptionsState] = useState({ selectedOptions: {}, totalPrice: model?.price || 0, allRequiredSelected: !hasOptions, combinationInStock: true });
  const recentlyViewed = useRecentlyViewed(
    model && { path: `/wellside-canopies/${model.slug}`, name: model.name, thumb: model.thumb, price: model.price, priceFrom: model.priceFrom, priceOnRequest: model.priceOnRequest }
  );

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!model) return <Navigate to="/wellside-canopies" replace />;

  const canBuyNow = !model.priceOnRequest && model.inStock !== false;
  const related = models.filter((m) => m.slug !== model.slug).slice(0, 3);
  const gallery = (model.gallery && model.gallery.length > 0 ? model.gallery : [model.hero || model.thumb]).map(normalizeGalleryImage);
  const features = model.features || WELLSIDE_FEATURES;

  return (
    <div data-testid="wellside-detail-page">
      <SEO
        path={`/wellside-canopies/${model.slug}`}
        title={model.seoTitle || `${model.name} Aluminium Canopy · Bushtech NZ`}
        description={model.seoDescription || `${model.name} — ${model.tagline}. ${model.intro.slice(0, 140)}`}
        image={model.hero || model.thumb}
        jsonLd={[
          buildProductJsonLd({ name: model.name, description: model.tagline, image: model.hero || model.thumb, price: model.price, priceOnRequest: model.priceOnRequest, inStock: model.inStock, url: `/wellside-canopies/${model.slug}` }),
          buildBreadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Wellside Canopies", path: "/wellside-canopies" },
            { name: model.name },
          ]),
        ]}
      />

      <section className="relative min-h-[60svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={model.hero || model.thumb} alt={model.name} className="w-full h-full" desktopPosition={model.heroPosition || "center 20%"} mobilePosition={model.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/wellside-canopies" data-testid="wellside-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)]">
              ← Wellside Canopies
            </Link>
            {model.badge && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                {model.badge}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-4xl">
            {model.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-5">
            {model.variants} — {model.tagline}
          </p>
          <div className="mt-8 flex items-start gap-6 flex-wrap">
            <div>
              <p className="font-display text-4xl md:text-5xl text-white leading-none">
                {model.priceOnRequest ? (
                  <span className="text-2xl font-mono uppercase tracking-widest text-white/80">Enquire For Price</span>
                ) : (
                  <>
                    {(model.priceFrom && !hasOptions) && <span className="text-lg font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                    ${fmt(hasOptions ? optionsState.totalPrice : model.price)}
                  </>
                )}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                {model.priceOnRequest ? "" : "GST included · "}{model.availability}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              {/* "Enquire" leads for this category — this is a considered $5-8k
                  purchase and 30 days of production checkout/cart data showed
                  zero canopy Buy-Now attempts (only smaller accessories convert
                  that way), so the enquiry path is the real primary conversion
                  goal here, not an afterthought next to Buy Now. */}
              <a href="#enquire" data-testid="wellside-cta-enquire" className="btn-primary">Enquire About This <ArrowUpRight size={14} /></a>
              {canBuyNow && (
                <>
                  <BuyNowButton
                    productPath={`/wellside-canopies/${model.slug}`}
                    testId="wellside-cta-buy-now"
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                    disabledHint={hasOptions && optionsState.allRequiredSelected && !optionsState.combinationInStock ? "" : "Select all options below to continue"}
                  />
                  <AddToCartButton
                    productPath={`/wellside-canopies/${model.slug}`}
                    testId="wellside-cta-add-to-cart"
                    name={model.name}
                    thumb={model.thumb}
                    unitPrice={hasOptions ? optionsState.totalPrice : model.price}
                    priceFrom={model.priceFrom && !hasOptions}
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                  />
                </>
              )}
            </div>
          </div>
          {hasOptions && (
            <div className="mt-8 max-w-lg">
              <ProductOptions optionGroups={model.optionGroups} variantStock={model.variantStock || []} basePrice={model.price} onChange={setOptionsState} />
            </div>
          )}
          {/* Hero Trust Badge Stack — approved for full rollout across all Wellside Canopy products. */}
          <HeroTrustBadgesV2 />
        </div>
      </section>

      <section className="container-x pt-16 md:pt-20 pb-8 md:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={model.name} />
          </div>
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Canopy</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">{model.intro}</p>
              {model.importerNote && (
                <div data-testid="wellside-importer-note" className="mt-5 pl-4 border-l-2 border-[var(--brand-primary)] flex items-start gap-3">
                  <Package size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={1.8} />
                  <p className="text-sm md:text-base text-white/70 leading-relaxed">{model.importerNote}</p>
                </div>
              )}
            </div>
            <div className="border-t border-[var(--border-default)] pt-8">
              <p className="overline mb-5">— Key Features</p>
              <ul className="space-y-3">
                {features.map((f, i) => (
                  <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                    <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{renderBoldSegments(f)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT'S DIFFERENT */}
      <WhyItsDifferentSection contentSections={model.contentSections} containerClassName="pt-8 md:pt-10 pb-16 md:pb-20" />

      {/* SPECS */}
      {model.specs && model.specs.length > 0 && (
        <section className="border-y border-[var(--border-default)] bg-[var(--bg-main)]">
          <div className="container-x py-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <p className="overline mb-4">— Specifications</p>
                <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                  THE <span className="text-[var(--brand-primary)]">NUMBERS.</span>
                </h2>
              </div>
              <div className="lg:col-span-8">
                <dl className="divide-y divide-[var(--border-default)] border-t border-b border-[var(--border-default)]">
                  {model.specs.map(([k, v], i) => (
                    <div key={k || i} data-testid={`spec-row-${i}`} className="grid grid-cols-3 gap-4 py-4">
                      <dt className="font-mono text-xs uppercase tracking-wider text-[var(--brand-primary)] col-span-1">{k}</dt>
                      <dd className="text-base md:text-lg text-white col-span-2">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TODO: "How We Compare" competitor table - draft data needs user review/correction
          before publishing (see /app/memory/ROADMAP.md). Re-enable by uncommenting below. */}
      {/* <CompetitorComparisonTable /> */}

      <section className="container-x pb-16 md:pb-20 border-t border-[var(--border-default)] pt-16 md:pt-20">
        <div className="mb-10 max-w-3xl">
          <p className="overline mb-3">— One Canopy, Two Jobs</p>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
            FOR SERIOUS OVERLANDERS<br />
            AND <span className="text-[var(--brand-primary)]">HARD-WORKING TRADIES.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          <div data-testid="persona-tradie" className="bg-[var(--bg-secondary)] p-6 md:p-8">
            <HardHat size={24} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
            <h3 className="font-display text-xl md:text-2xl text-white mb-3">The Tradie</h3>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
              A 300kg dynamic roof load capacity, internally braced double locking doors and central-locking compatibility mean your tools stay secure. We are almost never requested for break-in related spare parts — indicating that Bushtech canopies are simply not being targeted when much easier targets (plastic & fibreglass canopies) are just around the corner. It's roof-rack ready too, for ladder racks and job-specific fit-outs. Some of the biggest gullwing side-access doors on the market make a transformational difference to loading and unloading gear all day — rubber seals are mounted on the doors themselves, not the frame, so you can take full advantage of that huge opening without tearing the rubber seals the way you would on a frame-mounted setup.
            </p>
          </div>
          <div data-testid="persona-overlander" className="bg-[var(--bg-secondary)] p-6 md:p-8">
            <Compass size={24} className="text-[var(--brand-primary)] mb-4" strokeWidth={1.6} />
            <h3 className="font-display text-xl md:text-2xl text-white mb-3">The Overlander</h3>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
              Offering the highest dynamic load rating of any canopy in NZ, by far. Engineered to carry a fully-loaded roof tent through extended genuinely hard off-roading, not just on-road. We also stock overland-specific roof racks to fit the tracks on the Bushtech canopy, purpose-suited for mounting roof tents and awnings. Best-in-class dust and weather sealing keeps the interior clean on long dusty routes that fill lesser canopies with dust within a few kilometres. You can also add Bushtech overland accessories to the canopy over time — a growing range slots straight into extrusions built into the canopy.
            </p>
          </div>
        </div>
      </section>

      <section className="container-x pb-16 md:pb-20">
        <Link
          to="/wellside-accessories"
          data-testid="wellside-accessories-callout"
          className="group flex flex-col md:flex-row md:items-center justify-between gap-6 border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)] p-6 md:p-8"
        >
          <div className="flex items-start gap-4">
            <Package size={26} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={1.6} />
            <div>
              <h3 className="font-display text-lg md:text-xl text-white leading-tight">Every canopy is supported by an extensive range of accessories.</h3>
              <p className="text-base text-[var(--text-secondary)] mt-2 leading-relaxed max-w-2xl">
                Central locking, dust sealing kits, security mesh, K9 platform racks, storage systems and more — purpose-matched to fit a Bushtech canopy straight out of the box.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors shrink-0">
            View Accessories <ArrowUpRight size={14} />
          </span>
        </Link>
      </section>

      {related.length > 0 && (
        <section className="container-x pb-16 md:pb-20 border-t border-[var(--border-default)] pt-16 md:pt-20">
          <div className="mb-8">
            <p className="overline mb-3">— Other Configurations</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              MORE <span className="text-[var(--brand-primary)]">WELLSIDE CANOPIES.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/wellside-canopies/${r.slug}`} data-testid={`related-wellside-${i}`} className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.priceOnRequest ? "Enquire" : `${r.priceFrom ? "FROM " : ""}$${fmt(r.price)}`}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewedStrip items={recentlyViewed} />

      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Get A Quote</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br /><span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] text-base md:text-lg mt-6 leading-relaxed">
                Vehicle spec confirmation, lead times, install options, freight to your region — get proper answers.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={`Wellside Canopy — ${model.name}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
